/**
 * One-shot: restack the saved "Options" workspace onto the full desk
 * (every options pane, workflow order). The previous seed was Trade +
 * Chain, then Trade + Chain + Recommend; both hid the rest behind +.
 *
 * Always rewrites on first run — a tree that already has `opt:trade`
 * is exactly the half-desk this is replacing.
 *
 * Flagged by `mpp:migrate:options-full-desk:v1`.
 */
import { safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safeLocalStorage";
import { optionsDeskTree } from "@/lib/workspace/optionsDesk";
import type { WorkspacePage } from "@/lib/workspace/workspacePages";

const FLAG = "mpp:migrate:options-full-desk:v1";
const LIST_KEY = "mpp:workspaces:v1";
const PAGE_NAME = "options";
const DP_SEP = "\u0000@dp:";

export function migrateOptionsFullDesk(): void {
  if (typeof window === "undefined") return;
  if (safeLocalStorageGet(FLAG) === "1") return;

  let pool: WorkspacePage[] = [];
  try {
    const raw = safeLocalStorageGet(LIST_KEY);
    if (raw) {
      const arr = JSON.parse(raw) as WorkspacePage[];
      if (Array.isArray(arr)) pool = arr;
    }
  } catch {
    pool = [];
  }

  const optionsIds = new Set<string>();
  let poolChanged = false;
  const nextPool: WorkspacePage[] = pool.map((p) => {
    if (!p?.root) return p;
    if (typeof p.name !== "string" || p.name.trim().toLowerCase() !== PAGE_NAME) return p;
    optionsIds.add(p.id);
    poolChanged = true;
    return { ...p, root: optionsDeskTree(), updatedAt: Date.now() };
  });
  if (poolChanged) safeLocalStorageSet(LIST_KEY, JSON.stringify(nextPool));

  try {
    const writes: Array<{ key: string; value: string }> = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.includes("mpp:ws-tree:")) continue;
      if (![...optionsIds].some((id) => key.includes(id))) continue;
      writes.push({ key, value: JSON.stringify(optionsDeskTree()) });
    }
    for (const { key, value } of writes) {
      safeLocalStorageSet(key.includes(DP_SEP) ? key.split(DP_SEP)[0]! : key, value);
      if (key.includes(DP_SEP)) {
        try {
          window.localStorage.setItem(key, value);
        } catch {
          /* quota — logical write above is the one that matters */
        }
      }
    }
  } catch {
    /* pool migration still stands */
  }

  safeLocalStorageSet(FLAG, "1");
}
